import * as Sentry from '@sentry/browser'
import axios, { AxiosError, AxiosRequestConfig, CancelTokenSource } from 'axios'
import { computed, makeObservable } from 'mobx'

import buildUrl from 'modules/core/lib/buildUrl'
import { Model } from 'modules/core/lib/mobx-rest'

import type { IResource, ResourceData, Status } from './Resource'

/**
 * Create Params are used as the input to the Content API.
 */
export interface CreateParams {
  report_id?: string | null | undefined
  template_id?: string | null | undefined
  fallback: boolean
  sql_configuration?: string | null | undefined
  sql_query?: string | null | undefined
  json_query?: string | null | undefined
}

type ErrorTypes =
  | 'SyntaxError'
  | 'DeprecatedError'
  | 'MissingTable'
  | 'ResultSizeLimitExceeded'
  | 'Unexpected'

/**
 * ContentError returned by the API.
 *
 * This is a semantic error to inform of the following problems:
 * - End-Of-Life: Tables in Reports can be deprecated, when that happens the API returns a ContentError.
 * - Missing columns or tables: When user has specified columns that don't match the names of the db columns.
 * - Syntax error.
 */
export class ContentError extends Error {
  error: string
  error_type: ErrorTypes
  missing_tables?: string[]
  missing_fields?: string[]
  reportId?: string | null | undefined
  isSQL?: boolean | null | undefined

  constructor(
    error: string,
    error_type: ErrorTypes,
    missing_tables?: string[],
    missing_fields?: string[],
    reportId?: string | null | undefined,
    isSQL?: boolean | null | undefined
  ) {
    super('Content error') // This displays in the FE!
    Object.setPrototypeOf(this, ContentError.prototype)
    this.error = error
    this.error_type = error_type
    this.missing_tables = missing_tables
    this.missing_fields = missing_fields
    this.reportId = reportId
    this.isSQL = isSQL
  }
}

/**
 * Content represents the Content of a report.
 *
 * The content of a report is always the rows of the data to display
 * and metadata about the computation process.
 */
class Content extends Model<IResource> {
  constructor(attr: IResource = {}) {
    super(attr)
    /**
     * Creating a new Axios instance to avoid error interceptors
     * that are specific of Athena in the global Axios.
     * FIXME: Inject the axios instance
     */
    this.attributes.contentAxios = axios.create()
    makeObservable(this, {
      rows: computed,
      engine: computed,
      status: computed,
      lastUpdated: computed,
    })
  }

  static urlRoot() {
    return '/api/rest/reports'
  }

  /**
   * Create Report Content
   * @param data
   * @param cancelToken
   * @returns [ResourceData]
   * @throws [ContentError] When the api returns 4xx | 5xx
   */
  async create(data: CreateParams, cancelToken?: CancelTokenSource | null) {
    const params: AxiosRequestConfig = {
      withCredentials: true,
    }
    if (cancelToken) {
      params['cancelToken'] = cancelToken.token
    }
    let response
    try {
      response = await axios.create().post(buildUrl(`${Content.staticUrl()}/content`), data, params)
    } catch (e) {
      if (axios.isCancel(e)) {
        throw e
      } else if ((e as AxiosError).response) {
        const error = e as AxiosError

        if (error.response == null) {
          throw e
        }

        switch (error.response.status) {
          case 400:
            const errorMessage = error.response.data.errors?.error?.[0]

            if (errorMessage?.includes('Result size limit exceeded')) {
              throw new ContentError(errorMessage, 'ResultSizeLimitExceeded', undefined, undefined)
            }
          case 404:
            throw new ContentError(
              error.response.data.error,
              'MissingTable',
              error.response.data.missing_tables,
              error.response.data.missing_fields
            )
          case 422:
            throw new ContentError(
              error.response.data.error,
              'SyntaxError',
              undefined,
              undefined,
              data.report_id,
              data.sql_query != null && data.sql_query != ''
            )
        }

        throw new ContentError(error.response.data.error, 'Unexpected')
      } else if ((e as AxiosError).request) {
        // The request was made but no response was received
        // `e.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        Sentry.captureException(e)
        throw new ContentError('No response', 'Unexpected')
      } else {
        // Something happened in setting up the request that triggered an Error
        Sentry.captureException(e)
        throw new ContentError('No connection error', 'Unexpected')
      }
    }

    const lastUpdatedHeader =
      response.data.last_update && new Date(Date.parse(response.data.last_update))

    const parsedResponse: ResourceData = {
      lastUpdated: lastUpdatedHeader,
      engine: response.data.engine,
      rows: response.data.rows,
      status: response.data.status,
    }
    return parsedResponse
  }

  get lastUpdated(): Date | null | undefined {
    return this.attributes.lastUpdated
  }

  get engine(): string | null | undefined {
    return this.attributes.engine
  }

  get rows(): IResource[] {
    return this.attributes.rows
  }

  get status(): Status {
    return this.attributes.status
  }
}

export default Content
